import type { ChangeEvent, FormEvent } from "react"
import { Link, useParams } from "react-router-dom"
import Loading from "@/components/common/Loading"
import { useAdminTournamentForm } from "@/domain/admin/hooks/AdminTournamentFormHook"
import { SPORT_OPTIONS } from "@/domain/fight/model/Fight"
import { TOURNAMENT_STATUS_OPTIONS } from "@/domain/tournament/model/Tournament"
import type { TournamentFormInput } from "@/domain/tournament/model/Tournament"

const FIELD_CLASS = "focus-ring app-field"

/**
 * 대회 등록 · 수정 화면.
 *
 * 경로 파라미터에 id 가 있으면 수정, 없으면(`/admin/tournaments/new`) 등록이다.
 */
export default function AdminTournamentFormPage() {
  const { id = "" } = useParams()
  const tournamentId = id === "new" ? "" : id

  const {
    input,
    errors,
    errorMessage,
    isLoading,
    isModifyMode,
    savedId,
    setField,
    toggleSport,
    submit,
  } = useAdminTournamentForm(tournamentId)

  function onChangeField(
    field: keyof TournamentFormInput,
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) {
    setField(field, event.target.value)
  }

  function onSubmitForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  return (
    <div className="app-page-admin max-w-[880px]">
      <p className="app-txt-eyebrow text-app-primary-600">TOURNAMENT</p>
      <h2 className="app-txt-h1 mt-2">
        {isModifyMode ? "대회 수정" : "새 대회 등록"}
      </h2>
      <p className="app-txt-body2 mt-2 text-app-text-200">
        접수 마감은 대회 시작 전이어야 하고, 개최 종목은 1개 이상 선택해야
        합니다.
      </p>

      {isLoading && <Loading label="처리 중" />}

      {savedId && (
        <div className="app-alert app-alert-success mt-5">
          <p
            role="status"
            className="app-txt-body2 font-bold"
          >
            대회가 저장되었습니다.
          </p>
          <Link
            to="/admin/tournaments"
            className="focus-ring app-txt-button mt-2 inline-block hover:underline"
          >
            대회 목록으로 →
          </Link>
        </div>
      )}

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mt-5"
        >
          {errorMessage}
        </p>
      )}

      <form
        onSubmit={onSubmitForm}
        noValidate
        className="app-card mt-6 flex flex-col gap-5"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="tournament-name"
            className="app-label"
          >
            대회명
            <span
              aria-hidden="true"
              className="text-app-primary-500"
            >
              {" "}
              *
            </span>
          </label>
          <input
            id="tournament-name"
            type="text"
            value={input.name}
            maxLength={200}
            onChange={(event) => onChangeField("name", event)}
            aria-invalid={Boolean(errors.name)}
            className={FIELD_CLASS}
          />
          {errors.name && (
            <p
              role="alert"
              className="app-field-error"
            >
              {errors.name}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="tournament-venue"
            className="app-label"
          >
            개최 장소
          </label>
          <input
            id="tournament-venue"
            type="text"
            value={input.venue}
            maxLength={300}
            onChange={(event) => onChangeField("venue", event)}
            className={FIELD_CLASS}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="tournament-start-at"
              className="app-label"
            >
              대회 시작일시
            </label>
            <input
              id="tournament-start-at"
              type="datetime-local"
              value={input.startAt}
              onChange={(event) => onChangeField("startAt", event)}
              aria-invalid={Boolean(errors.startAt)}
              className={FIELD_CLASS}
            />
            {errors.startAt && (
              <p
                role="alert"
                className="app-field-error"
              >
                {errors.startAt}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="tournament-end-at"
              className="app-label"
            >
              대회 종료일시
            </label>
            <input
              id="tournament-end-at"
              type="datetime-local"
              value={input.endAt}
              onChange={(event) => onChangeField("endAt", event)}
              aria-invalid={Boolean(errors.endAt)}
              className={FIELD_CLASS}
            />
            {errors.endAt && (
              <p
                role="alert"
                className="app-field-error"
              >
                {errors.endAt}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="tournament-entry-open-at"
              className="app-label"
            >
              접수 시작일시
            </label>
            <input
              id="tournament-entry-open-at"
              type="datetime-local"
              value={input.entryOpenAt}
              onChange={(event) => onChangeField("entryOpenAt", event)}
              aria-invalid={Boolean(errors.entryOpenAt)}
              className={FIELD_CLASS}
            />
            {errors.entryOpenAt && (
              <p
                role="alert"
                className="app-field-error"
              >
                {errors.entryOpenAt}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="tournament-entry-close-at"
              className="app-label"
            >
              접수 마감일시
            </label>
            <input
              id="tournament-entry-close-at"
              type="datetime-local"
              value={input.entryCloseAt}
              onChange={(event) => onChangeField("entryCloseAt", event)}
              aria-invalid={Boolean(errors.entryCloseAt)}
              className={FIELD_CLASS}
            />
            {errors.entryCloseAt && (
              <p
                role="alert"
                className="app-field-error"
              >
                {errors.entryCloseAt}
              </p>
            )}
          </div>
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="app-label">
            개최 종목
            <span
              aria-hidden="true"
              className="text-app-primary-500"
            >
              {" "}
              *
            </span>
          </legend>

          <div className="mt-1 flex flex-wrap gap-3">
            {SPORT_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="app-txt-body2 flex cursor-pointer items-center gap-2 border border-app-secondary-600 px-3 py-2.5 transition hover:bg-app-secondary-300"
              >
                <input
                  type="checkbox"
                  checked={input.sports.includes(option.value)}
                  onChange={() => toggleSport(option.value)}
                  className="focus-ring size-4 accent-app-primary-500"
                />
                {option.label}
              </label>
            ))}
          </div>

          {errors.sports && (
            <p
              role="alert"
              className="app-field-error"
            >
              {errors.sports}
            </p>
          )}
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="tournament-status"
            className="app-label"
          >
            대회 상태
          </label>
          <select
            id="tournament-status"
            value={input.status}
            onChange={(event) => onChangeField("status", event)}
            className={FIELD_CLASS}
          >
            {TOURNAMENT_STATUS_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="tournament-notice"
            className="app-label"
          >
            안내사항
          </label>
          <textarea
            id="tournament-notice"
            value={input.notice}
            rows={5}
            onChange={(event) => onChangeField("notice", event)}
            placeholder="계체 시간, 준비물 등 참가자에게 알릴 내용"
            className={FIELD_CLASS}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Link
            to="/admin/tournaments"
            className="focus-ring app-btn app-btn-outline"
          >
            목록으로
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className="focus-ring app-btn app-btn-primary"
          >
            저장
          </button>
        </div>
      </form>
    </div>
  )
}
